// FIG Session (RESTYLE §4): the replay video and the stored frames, framed in the specimen plate.
//   default SessionFig (FigProps) · meta(ctx) → { id: "session", label: "Session", count: stored frames,
//     empty: live, or neither a replay nor a stored frame }
//   Two journal cells: s8 = the player (or the last frame) and the filmstrip; s4 side = the session sheet.
//   The player plays the presigned S3 URL and falls back to the same-origin stream on any error, keeping its position;
//   poster = the last stored frame; 1× / 2× / 4×; Space, Left, Right, Home, End while the video has focus.
//   <FrameViewer jobId frames index t0 onIndex onClose />   the full-size frame dialog (FIG Artifacts uses it too)
//   sessionFrames(artifacts) → the screenshot artifacts, oldest first · replayOf(artifacts) → the replay artifact
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent, type SyntheticEvent } from "react";
import { Alert, Button, FigHead, Kv, Plate, Seg, Sep, figTitleId, type KvRow } from "@/components/ui";
import { PRESIGN_EXPIRES_S, pickArtifact, usePresignedDownload } from "@/hooks";
import { api, isApiError } from "@/lib/api";
import { bytes, clock, count, plural, rel, utc } from "@/lib/format";
import type { FigMeta, FigProps, ReportCtx } from "@/lib/derive/types";
import type { Artifact } from "@/types";

export const sessionFrames = (artifacts: readonly Artifact[]): Artifact[] =>
  artifacts.filter((a) => a.kind === "screenshot").sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));

const replayOf = (artifacts: readonly Artifact[]): Artifact | undefined => pickArtifact(artifacts, "replay");

export function meta(ctx: ReportCtx): FigMeta {
  const frames = sessionFrames(ctx.artifacts).length;
  const replay = !!replayOf(ctx.artifacts);
  return { id: "session", label: "Session", count: frames || undefined, empty: ctx.mode === "live" || (!replay && frames === 0) };
}

const frameURL = (jobId: string, a: Artifact) => api.artifactURL(jobId, a.sha256);
const atRun = (a: Artifact, t0: number | null) => {
  const t = Date.parse(a.created_at);
  return t0 !== null && Number.isFinite(t) ? rel(t - t0, 1) : Number.isFinite(t) ? `${utc(t, "time")} UTC` : "—";
};

// ---- the replay source: presigned first, same-origin on any failure ---------------------------------------------
type Source = { url: string; via: "presigned" | "same-origin"; why?: string; expires?: number };

/** One presigned stream failed this session: the store's S3 objects are not reachable, so go same-origin. */
let presignFailed = false;

function useReplaySource(jobId: string, art: Artifact | undefined) {
  const sha = art?.sha256;
  const [src, setSrc] = useState<Source | null>(null);
  useEffect(() => {
    if (!sha) {
      setSrc(null);
      return;
    }
    const same: Source = { url: api.artifactURL(jobId, sha), via: "same-origin" };
    if (presignFailed) {
      setSrc({ ...same, why: "presigned links failed earlier" });
      return;
    }
    let off = false;
    api.presigned(jobId, sha, PRESIGN_EXPIRES_S).then(
      (r) => {
        if (off) return;
        setSrc(r.url ? { url: new URL(r.url, window.location.href).href, via: "presigned", expires: r.expires_in } : { ...same, why: "no presigned URL" });
      },
      (e) => {
        // a store that is not S3-backed stays that way: ask once per page load
        if (isApiError(e) && e.status === 503) presignFailed = true;
        if (off) return;
        setSrc({ ...same, why: isApiError(e) && e.status === 503 ? "the store is not S3-backed" : isApiError(e) ? `presign answered ${e.status}` : "presign failed" });
      },
    );
    return () => {
      off = true;
    };
  }, [jobId, sha]);
  const fallback = useCallback(() => {
    presignFailed = true;
    if (sha) setSrc({ url: api.artifactURL(jobId, sha), via: "same-origin", why: "the presigned URL did not play" });
  }, [jobId, sha]);
  return { src, fallback };
}

// ---- the full-size frame dialog ------------------------------------------------------------------------------
export function FrameViewer({
  jobId,
  frames,
  index,
  t0,
  onIndex,
  onClose,
}: {
  jobId: string;
  frames: readonly Artifact[];
  index: number | null;
  t0: number | null;
  onIndex: (i: number) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const { download, pending } = usePresignedDownload();
  const open = index !== null && index >= 0 && index < frames.length;
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    else if (!open && d.open) d.close();
  }, [open]);
  const f = open ? frames[index] : undefined;
  const step = (by: number) => {
    if (index === null) return;
    const next = Math.max(0, Math.min(frames.length - 1, index + by));
    if (next !== index) onIndex(next);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDialogElement>) => {
    e.stopPropagation(); // the viewer owns the keyboard while open
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      step(-1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      step(1);
    }
  };
  const onClick = (e: MouseEvent<HTMLDialogElement>) => {
    if (e.target === e.currentTarget) onClose();
  };
  return (
    <dialog
      ref={ref}
      aria-labelledby="frame-viewer-t"
      onClose={onClose}
      onKeyDown={onKeyDown}
      onClick={onClick}
      className="m-auto w-[min(1180px,calc(100vw-32px))] max-h-[calc(100dvh-32px)] overflow-y-auto border border-rule-strong bg-raised p-0 text-ink shadow-pop backdrop:bg-paper/75"
    >
      {f && index !== null && (
        <div className="px-4 pt-4 pb-5 min-[720px]:px-6">
          <div className="flex items-baseline justify-between gap-4">
            <p id="frame-viewer-t" className="lens-eyebrow">
              Frame {index + 1} of {frames.length}
              <Sep />
              {atRun(f, t0)}
            </p>
            <button type="button" className="copy-text" onClick={onClose}>
              close <kbd>Esc</kbd>
            </button>
          </div>
          <Plate className="mt-3">
            <img
              className="screen max-h-[calc(100dvh-210px)] object-contain"
              src={frameURL(jobId, f)}
              alt={`Guest screen, frame ${index + 1} of ${frames.length}, stored at ${atRun(f, t0)}`}
              draggable={false}
            />
          </Plate>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <span className="meta min-w-0" translate="no">
              {f.name}
              <Sep />
              {bytes(f.size)}
              <Sep />
              stored {utc(f.created_at, "time")} UTC
            </span>
            <span className="flex flex-wrap gap-2">
              <Button size="sm" disabled={index === 0} onClick={() => step(-1)}>
                Previous
              </Button>
              <Button size="sm" disabled={index === frames.length - 1} onClick={() => step(1)}>
                Next
              </Button>
              <Button size="sm" icon="download" disabled={pending === f.sha256} onClick={() => void download(jobId, f)}>
                {pending === f.sha256 ? "Preparing…" : "Download"}
              </Button>
            </span>
          </div>
        </div>
      )}
    </dialog>
  );
}

// ---- the filmstrip -------------------------------------------------------------------------------------------
function Filmstrip({ jobId, frames, t0, onOpen }: { jobId: string; frames: readonly Artifact[]; t0: number | null; onOpen: (i: number) => void }) {
  return (
    <ol className="mt-6 grid auto-cols-[minmax(132px,1fr)] grid-flow-col gap-3 overflow-x-auto pb-2 [scrollbar-width:thin]" aria-label="Stored frames">
      {frames.map((f, i) => (
        <li key={f.sha256} className="min-w-0">
          <button
            type="button"
            className="group block w-full text-left"
            onClick={() => onOpen(i)}
            aria-label={`Open frame ${i + 1} of ${frames.length}, ${atRun(f, t0)}, full size`}
          >
            {/* a thumbnail gets one hairline; the ticked specimen plate is for the replay and the live screen */}
            <span className="thumb">
              <img className="screen aspect-[16/10] bg-sunk object-cover" src={frameURL(jobId, f)} alt="" loading="lazy" draggable={false} />
            </span>
            <span className="mt-2 flex items-baseline justify-between gap-2 font-mono text-meta">
              <span className="text-ink tabular-nums group-hover:underline group-hover:underline-offset-[.2em]">{atRun(f, t0)}</span>
              <span className="truncate text-ink-faint" translate="no">
                {f.name}
              </span>
            </span>
          </button>
        </li>
      ))}
    </ol>
  );
}

// ---- the player ----------------------------------------------------------------------------------------------
type Speed = "1" | "2" | "4";
const SPEEDS = [
  { value: "1" as const, label: "1×" },
  { value: "2" as const, label: "2×" },
  { value: "4" as const, label: "4×" },
];
type VideoMeta = { duration: number; w: number; h: number };
const PLAYER_KEYS = new Set([" ", "ArrowLeft", "ArrowRight", "Home", "End"]);

function Player({
  jobId,
  replay,
  poster,
  onMeta,
  onSource,
}: {
  jobId: string;
  replay: Artifact;
  poster?: string;
  onMeta: (m: VideoMeta) => void;
  onSource: (s: Source | null) => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const { src, fallback } = useReplaySource(jobId, replay);
  const [speed, setSpeed] = useState<Speed>("1");
  const [failed, setFailed] = useState<string | null>(null);
  const [aspect, setAspect] = useState("16 / 10");
  const resume = useRef<{ t: number; play: boolean } | null>(null);

  useEffect(() => onSource(src), [src, onSource]);
  useEffect(() => {
    if (video.current) video.current.playbackRate = Number(speed);
  }, [speed, src]);

  const loaded = (e: SyntheticEvent<HTMLVideoElement>) => {
    const v = e.currentTarget;
    v.playbackRate = Number(speed);
    if (v.videoWidth && v.videoHeight) setAspect(`${v.videoWidth} / ${v.videoHeight}`);
    onMeta({ duration: v.duration, w: v.videoWidth, h: v.videoHeight });
    const r = resume.current;
    resume.current = null;
    if (r) {
      v.currentTime = Math.min(r.t, Number.isFinite(v.duration) ? v.duration : r.t);
      if (r.play) void v.play().catch(() => undefined);
    }
  };
  const errored = (e: SyntheticEvent<HTMLVideoElement>) => {
    const v = e.currentTarget;
    if (src?.via === "presigned") {
      resume.current = { t: v.currentTime || 0, play: !v.paused };
      fallback();
      return;
    }
    const code = v.error?.code;
    setFailed(code === 4 ? "the browser cannot decode it" : code === 2 ? "the download failed" : "it could not be loaded");
  };
  // The player frame takes the keys. A focused <video> keeps Chromium's own media keys (it pauses any play()
  // started from a key it also handles), so there the keys only stop short of the report's shortcuts.
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const v = video.current;
    if (!v || e.altKey || e.ctrlKey || e.metaKey) return;
    if (e.target === v) {
      if (PLAYER_KEYS.has(e.key)) e.stopPropagation();
      return;
    }
    if (e.target !== e.currentTarget) return;
    const end = Number.isFinite(v.duration) ? v.duration : v.currentTime;
    switch (e.key) {
      case " ":
        if (v.paused) void v.play().catch(() => undefined);
        else v.pause();
        break;
      case "ArrowLeft":
        v.currentTime = Math.max(0, v.currentTime - 5);
        break;
      case "ArrowRight":
        v.currentTime = Math.min(end, v.currentTime + 5);
        break;
      case "Home":
        v.currentTime = 0;
        break;
      case "End":
        v.currentTime = end;
        break;
      default:
        return;
    }
    e.preventDefault();
    e.stopPropagation(); // the report's own keys stay behind a focused player
  };

  return (
    <>
      <Plate>
        <div
          className="w-full"
          tabIndex={0}
          role="group"
          aria-label="Replay player. Space plays or pauses; Left and Right step 5 seconds; Home and End jump to the ends."
          onKeyDown={onKey}
        >
          {failed ? (
            <div className="grid w-full place-items-center bg-sunk p-6" style={{ aspectRatio: aspect }}>
              <Alert head="Couldn't play the replay">
                replay.mp4 ({bytes(replay.size)}): {failed}. Download replay saves the file instead.
              </Alert>
            </div>
          ) : (
            <video
              ref={video}
              key={src?.url ?? "pending"}
              className="screen bg-sunk"
              style={{ aspectRatio: aspect }}
              src={src?.url}
              poster={poster}
              controls
              controlsList="noplaybackrate"
              disablePictureInPicture
              preload="metadata"
              playsInline
              aria-label="Replay of the guest's screen"
              onLoadedMetadata={loaded}
              onError={errored}
            />
          )}
        </div>
      </Plate>
      <div className="plate-cap items-center">
        <span className="max-[480px]:hidden">
          <kbd>Space</kbd> plays or pauses
          <Sep />
          <kbd>Left</kbd> <kbd>Right</kbd> step 5 s
        </span>
        <span className="inline-flex items-center gap-2.5">
          speed
          <Seg label="Playback speed" options={SPEEDS} value={speed} onChange={setSpeed} className="[&>button]:min-w-[3.4em]" />
        </span>
      </div>
    </>
  );
}

// ---- the figure ----------------------------------------------------------------------------------------------
function guestSpan(ctx: ReportCtx): number | null {
  const tr = ctx.stream.transitions;
  const from = tr.find((t) => t.state === "booting" && Number.isFinite(t.ts))?.ts ?? tr.find((t) => t.state === "provisioning" && Number.isFinite(t.ts))?.ts;
  const endT = [...tr].reverse().find((t) => (t.state === "completed" || t.state === "cancelled" || t.state === "failed") && Number.isFinite(t.ts))?.ts;
  const to = endT ?? (ctx.job.ended_at ? Date.parse(ctx.job.ended_at) : NaN);
  return from !== undefined && Number.isFinite(to) && to > from ? to - from : null;
}

export default function SessionFig({ ctx, no }: FigProps) {
  const { job, artifacts } = ctx;
  const frames = useMemo(() => sessionFrames(artifacts), [artifacts]);
  const replay = replayOf(artifacts);
  const t0 = ctx.stream.t0;
  const last = frames[frames.length - 1];
  const [viewing, setViewing] = useState<number | null>(null);
  const [vmeta, setVmeta] = useState<VideoMeta | null>(null);
  const [source, setSource] = useState<Source | null>(null);
  const { download, pending } = usePresignedDownload();
  const span = guestSpan(ctx);

  const n = frames.length;
  const lastAt = last ? atRun(last, t0) : null;

  const rows: KvRow[] = [];
  if (replay) {
    rows.push([
      "Replay",
      <>
        <span translate="no">{replay.name}</span>
        <small> · {bytes(replay.size)}</small>
      </>,
    ]);
    rows.push([
      "Length",
      vmeta && Number.isFinite(vmeta.duration) ? (
        <>
          {clock(vmeta.duration * 1000)}
          {vmeta.w > 0 && (
            <small>
              {" "}
              · {vmeta.w} × {vmeta.h}
            </small>
          )}
        </>
      ) : null,
    ]);
    rows.push([
      "Covers",
      span !== null ? (
        <>
          {clock(span)} of guest time
          {vmeta && vmeta.duration > 0 && Number.isFinite(vmeta.duration) && <small> · about {(span / 1000 / vmeta.duration).toFixed(1)}× real time</small>}
        </>
      ) : null,
    ]);
    rows.push([
      "Stream",
      source ? (
        source.via === "presigned" ? (
          <>
            presigned URL<small> · {source.expires ? `${Math.round(source.expires / 60)} min` : "short-lived"}</small>
          </>
        ) : (
          <>
            same-origin<small>{source.why ? ` · ${source.why}` : ""}</small>
          </>
        )
      ) : null,
    ]);
  }
  rows.push([
    "Frames",
    n ? (
      <>
        {count(n)} stored
        <small> · last {lastAt}</small>
      </>
    ) : (
      <span className="text-ink-faint">none stored</span>
    ),
  ]);
  if (t0 !== null) rows.push(["Clock", <>T0 {utc(t0, "time")} <small>UTC · {ctx.stream.t0Source === "booting" ? "VM boot" : "sample start"}</small></>]);

  const onMeta = useCallback((m: VideoMeta) => setVmeta(m), []);
  const onSource = useCallback((s: Source | null) => setSource(s), []);
  const poster = last ? frameURL(job.id, last) : undefined;

  return (
    <>
      <section className="cell s8 fig" aria-labelledby={figTitleId(no)}>
        <FigHead
          no={no}
          label="Session"
          count={[replay && (vmeta && Number.isFinite(vmeta.duration) ? `replay ${clock(vmeta.duration * 1000)}` : "replay"), n && `${count(n)} ${plural(n, "frame")}`].filter(Boolean).join(" · ")}
        />
        <div className="fig-body">
          {replay ? (
            <Player jobId={job.id} replay={replay} poster={poster} onMeta={onMeta} onSource={onSource} />
          ) : (
            last && (
              <button type="button" className="block w-full text-left" onClick={() => setViewing(n - 1)} aria-label={`Open the last frame, ${lastAt}, full size`}>
                <Plate>
                  <img className="screen" src={frameURL(job.id, last)} alt={`Guest screen, last stored frame at ${lastAt}`} draggable={false} />
                </Plate>
              </button>
            )
          )}
          {n > 1 ? <Filmstrip jobId={job.id} frames={frames} t0={t0} onOpen={setViewing} /> : null}
        </div>
        {/* inside the cell: the side cell must stay the row's last child (it drops its right padding) */}
        <FrameViewer jobId={job.id} frames={frames} index={viewing} t0={t0} onIndex={setViewing} onClose={() => setViewing(null)} />
      </section>
      <section className="cell s4 side fig" aria-label="Session details">
        <p className="lens-eyebrow">Under the lens · session</p>
        <Kv rows={rows} className="mt-3" />
        <div className="mt-5 flex flex-wrap gap-2">
          {replay && (
            <Button icon="download" disabled={pending === replay.sha256} onClick={() => void download(job.id, replay)}>
              {pending === replay.sha256 ? "Preparing…" : "Download replay"}
            </Button>
          )}
          {last && (
            <Button variant="ghost" onClick={() => setViewing(n - 1)}>
              Last frame
            </Button>
          )}
        </div>
      </section>
    </>
  );
}
